import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { setSaving } from '../../redux/actions/saveData/saveData';
import { useReadOnly } from '../../utils/ReadOnlyContext';
import { BusinessOperationsKeys, farmingOption, noFormSelectedOption, radioSelectBusinessOperations } from '../../pages/BusinessOperations/BusinessOperations';
import { useBusinessOperationsState } from '../../utils/context/BusinessOperationsContext';
import { FormError, useFormErrorContext } from '../../utils/context/FormErrorContext';
import getFullJsonPayload from '../../utils/gev/getFullJsonPayload';
import ConfirmDialog from './ConfirmDialog';
import ErrorMessages from '../common/ErrorMessages/ErrorMessages';
import Radio from '../common/Radio/Radio';

export interface OverviewProps {
    resetErrCount: (operationKey: BusinessOperationsKeys) => void,
    handleTriggerValidation: (newValue: boolean) => void
}

export default function Overview(props: OverviewProps): JSX.Element {
    const dispatch = useDispatch()
    const { t } = useTranslation('gev')
    const { errors } = useFormErrorContext()
    const { isReadOnly } = useReadOnly()
    const { formData, setFormData } = useBusinessOperationsState()
    const [selectedOperation, setSelectedOperation] = useState<BusinessOperationsKeys>()
    const [showConfirmUnselectDialog, setShowConfirmUnselectDialog] = useState(false)
    const [formErrors, setFormErrors] = useState<FormError[]>([])

    useEffect(() => {
        if (errors.length > 0) {
            errors.forEach((error) => {
                if (error.primaryKey === null) {
                    setFormErrors(prevData => [...prevData, error])
                }
            })
        } else {
            setFormErrors([]);
        }
    }, [errors]);

    const handleSelectionChange = (operationKey: BusinessOperationsKeys, _selected: boolean) => {
        setFormErrors([]);
        setSelectedOperation(operationKey);
        const fullPayload = getFullJsonPayload(operationKey);
        switch (operationKey) {
            case noFormSelectedOption:
                if (formData.farmingOperationCommercialGrain.selected) {
                    setShowConfirmUnselectDialog(true)
                } else {
                    setFormData({
                        commercialGrainElevatorFacility: { selected: false },
                        feedMillingProcessingFacility: { selected: false },
                        otherAgriBusiness: { selected: false },
                        farmingOperationCommercialGrain: { selected: false },
                        noFormSelected: fullPayload
                    });
                }
                break;
            case farmingOption:
                if (formData.noFormSelected.selected) {
                    setShowConfirmUnselectDialog(true)
                } else {
                    setFormData({
                        commercialGrainElevatorFacility: { selected: false },
                        feedMillingProcessingFacility: { selected: false },
                        otherAgriBusiness: { selected: false },
                        farmingOperationCommercialGrain: fullPayload,
                        noFormSelected: { selected: false }
                    });
                }
                break;
            default:
                break;
        }
    }

    const handleUnselectDialogBox = () => {
        switch (selectedOperation) {
            case noFormSelectedOption:
                setFormData((prevData) => ({
                    ...prevData,
                    [selectedOperation as BusinessOperationsKeys]: getFullJsonPayload(selectedOperation),
                    farmingOperationCommercialGrain: { selected: false }
                }));
                props.resetErrCount(farmingOption as BusinessOperationsKeys);
                break;
            case farmingOption:
                setFormData((prevData) => ({
                    ...prevData,
                    [selectedOperation as BusinessOperationsKeys]: getFullJsonPayload(selectedOperation),
                    noFormSelected: { selected: false }
                }));
                props.resetErrCount(noFormSelectedOption as BusinessOperationsKeys);
                break;
            default:
                break;
        }
        dispatch(setSaving());
        props.handleTriggerValidation(false); // if user unselects a form, delete data from magnetar without triggering spectral
        setShowConfirmUnselectDialog(false);
    }

    return (
        <>
            {formErrors && <ErrorMessages errors={formErrors} />}
            <form>
                <div className='flex flex-row gev-gap'>
                    <div>{t("overview.heading")}</div>
                    {radioSelectBusinessOperations.length > 0 &&
                        (radioSelectBusinessOperations.map((key) => {
                            if (Object.keys(formData).length > 0 && Object.keys(formData).includes(key)) {
                                return (
                                    <div className="radio-text-container" key={key}>
                                        <div>{t('overview.radioSelect.' + key)}</div>
                                        <Radio name="selectedOperations"
                                            value={key}
                                            checked={formData[key as BusinessOperationsKeys].selected}
                                            onChange={(e) => handleSelectionChange(key as BusinessOperationsKeys, e.currentTarget.checked)}
                                            disabled={isReadOnly}
                                        />
                                    </div>
                                )
                            } else {
                                return null
                            }
                        }))
                    }
                </div>
                {
                    showConfirmUnselectDialog &&
                    <ConfirmDialog onClose={() => setShowConfirmUnselectDialog(false)}
                        onSubmit={handleUnselectDialogBox}
                        title={`${t('overview.areYouSureChangeSelection')}`}
                        body={t('overview.ifUnselect')}
                    />
                }
            </form>
        </>
    )
}
